if (!customElements.get('trust-benefits-bar')) {
  class TrustBenefitsBar extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-trust-benefits-track]');
      this.slides = Array.from(this.querySelectorAll('[data-trust-benefits-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-trust-benefits-dot]'));
      this.nextButton = this.querySelector('[data-trust-benefits-next]');
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.speed = (Number(this.dataset.speed) || 4) * 1000;
      this.index = 0;
      this.hovering = false;
      this.editorPaused = false;
      this.inView = true;
      this.programmatic = false;

      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      this.onScroll = this.onScroll.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
      this.onModeChange = this.onModeChange.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.nextButton?.addEventListener('click', this.onNext);
      this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
      this.mobileQuery.addEventListener('change', this.onModeChange);
      this.reducedMotion.addEventListener('change', this.onModeChange);
      window.addEventListener('resize', this.onModeChange);
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      if (this.canHover) {
        this.addEventListener('mouseenter', this.pauseForHover);
        this.addEventListener('mouseleave', this.resumeFromHover);
      }

      this.addEventListener('focusin', this.pauseForHover);
      this.addEventListener('focusout', this.onFocusOut);
      this.track.addEventListener('touchstart', this.pauseForHover, { passive: true });
      this.track.addEventListener('touchend', this.resumeFromTouch, { passive: true });

      this.observer = new IntersectionObserver(
        (entries) => {
          this.inView = entries.some((entry) => entry.isIntersecting);
          this.syncAutoplay();
        },
        { threshold: 0.4 }
      );
      this.observer.observe(this);
      this.updateDots();
      this.syncAutoplay();
    }

    disconnectedCallback() {
      this.stop();
      this.observer?.disconnect();
      this.track?.removeEventListener('scroll', this.onScroll);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
      this.mobileQuery?.removeEventListener('change', this.onModeChange);
      this.reducedMotion?.removeEventListener('change', this.onModeChange);
      window.removeEventListener('resize', this.onModeChange);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.removeEventListener('mouseenter', this.pauseForHover);
      this.removeEventListener('mouseleave', this.resumeFromHover);
      this.removeEventListener('focusin', this.pauseForHover);
      this.removeEventListener('focusout', this.onFocusOut);
      this.track?.removeEventListener('touchstart', this.pauseForHover);
      this.track?.removeEventListener('touchend', this.resumeFromTouch);
      window.clearTimeout(this.programmaticTimer);
      window.clearTimeout(this.touchTimer);
    }

    pauseForHover = () => {
      this.hovering = true;
      this.stop();
    };

    resumeFromHover = () => {
      this.hovering = false;
      this.syncAutoplay();
    };

    onFocusOut = (event) => {
      if (this.contains(event.relatedTarget)) return;
      this.hovering = false;
      this.syncAutoplay();
    };

    resumeFromTouch = () => {
      window.clearTimeout(this.touchTimer);
      this.touchTimer = window.setTimeout(() => {
        this.hovering = false;
        this.syncAutoplay();
      }, 600);
    };

    onModeChange() {
      this.updateDots();
      this.syncAutoplay();
    }

    onVisibility() {
      this.syncAutoplay();
    }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      const slide = event.target.closest('[data-trust-benefits-slide]');
      if (!slide) return;
      const index = this.slides.indexOf(slide);
      if (index < 0) return;
      this.editorPaused = true;
      this.stop();
      this.goTo(index, true);
    }

    onBlockDeselect(event) {
      if (!this.contains(event.target)) return;
      this.editorPaused = false;
      this.syncAutoplay();
    }

    isMobile() {
      return this.mobileQuery.matches;
    }

    canAutoplay() {
      return (
        this.autoplayEnabled &&
        this.slides.length > 1 &&
        this.isMobile() &&
        !this.reducedMotion.matches &&
        !document.hidden &&
        !this.hovering &&
        !this.editorPaused &&
        this.inView
      );
    }

    syncAutoplay() {
      if (this.canAutoplay()) {
        if (this.timer) return;
        this.timer = window.setInterval(() => this.goTo(this.index + 1), this.speed);
        return;
      }
      this.stop();
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    onNext(event) {
      event.preventDefault();
      this.goTo(this.index + 1);
      this.stop();
      this.syncAutoplay();
    }

    onDotClick(event) {
      event.preventDefault();
      const index = Number(event.currentTarget.dataset.index);
      if (Number.isNaN(index)) return;
      this.goTo(index);
      this.stop();
      this.syncAutoplay();
    }

    goTo(index, instant = false) {
      const count = this.slides.length;
      if (!count || !this.isMobile()) return;
      const next = ((index % count) + count) % count;
      this.index = next;
      this.updateDots();
      const slide = this.slides[next];
      const target = slide.offsetLeft;
      const token = (this.scrollToken || 0) + 1;
      this.scrollToken = token;
      this.programmatic = true;
      const release = () => {
        if (this.scrollToken !== token) return;
        this.programmatic = false;
      };
      this.track.addEventListener('scrollend', release, { once: true });
      this.track.scrollTo({
        left: target,
        behavior: 'auto',
      });
      window.clearTimeout(this.programmaticTimer);
      this.programmaticTimer = window.setTimeout(release, 900);
    }

    onScroll() {
      if (this.programmatic || !this.isMobile()) return;
      if (this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        const left = this.track.scrollLeft;
        let closest = 0;
        let smallest = Infinity;
        this.slides.forEach((slide, index) => {
          const distance = Math.abs(slide.offsetLeft - left);
          if (distance < smallest) {
            smallest = distance;
            closest = index;
          }
        });
        if (closest === this.index) return;
        this.index = closest;
        this.updateDots();
      });
    }

    updateDots() {
      const mobile = this.isMobile();
      this.dots.forEach((dot, index) => {
        if (index === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      this.slides.forEach((slide, index) => {
        const hidden = mobile && index !== this.index;
        slide.setAttribute('aria-hidden', hidden ? 'true' : 'false');
        slide.querySelectorAll('a').forEach((link) => {
          if (hidden) link.setAttribute('tabindex', '-1');
          else link.removeAttribute('tabindex');
        });
      });
    }
  }

  customElements.define('trust-benefits-bar', TrustBenefitsBar);
}
